import {makeAutoObservable, onBecomeObserved, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';
import * as yup from 'yup';

import {AirtableHomepageItem} from '../models/airtable';
import apiService from '../transport/apiService';

import { ShopSelectionStore } from './shopSelectionStore';

const ContentKey = [
  'TopBanner',
  'LoyaltyAd',
  'DiscoverFeature',
  'HeroBrands',
  'AlertBanner',
] as const;

type HomepageContentKey = typeof ContentKey[number];

const schema = yup
  .mixed()
  .test('is-known-homepage-content-key', 'Invalid content key', (value) => 
    ContentKey.includes(value as HomepageContentKey));

export class HomepageStore {
  shopSelectionStore: ShopSelectionStore;
  isLoading: boolean = false;
  data: Record<HomepageContentKey, AirtableHomepageItem> = {} as any;
  unknownData: Record<string, AirtableHomepageItem> = {};

  constructor(shopSelectionStore: ShopSelectionStore) {
    this.shopSelectionStore = shopSelectionStore;
    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'data', this.load);
  }

  private load = async () => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });
      const selectedStore = this.shopSelectionStore?.selectedStore;

      if (!selectedStore) {
        console.warn('Homepage - Abort Load, no store selected');
        return;
      }

      const data = await apiService.getHomepage(
        selectedStore?.store_number,
        selectedStore?.address?.administrative_area?.toUpperCase(),
      );

      runInAction(() => {
        const unknownData: Record<string, AirtableHomepageItem> = {};
        const processed = data.reduce((prev: HomepageStore['data'], cur) => {
          const key = cur.content;
          if (schema.isValidSync(key)) {
            prev[key as HomepageContentKey] = cur;
          } else {
            console.info('Homepage - Unknown content key:', key);
            unknownData[key] = cur;
          }
          return prev;
        }, {} as HomepageStore['data']);

        this.data = processed;
        this.unknownData = unknownData;
      });
    } catch (err) {
      console.error('Homepage - Content Error loading content', err);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}
